import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, map, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PayoutsService } from '../../../../core/services/payouts.service';
import { DialogService, NotifierService, Util } from '@wm-libs/core';
import { PageEvent } from '@angular/material/paginator';
import { ModifierPayouts, Payout, Payouts } from '../../../../core/interfaces/payouts.interface';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyDataComponent } from '../../../../core/components/empty-data/empty-data.component';
import { DataTableComponent } from '../../../../core/components/data-table/data-table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { WithdrawComponent } from '../../components/withdraw/withdraw.component';
import { NgClass, NgIf, AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-payouts',
  templateUrl: './payouts.component.html',
  styleUrls: ['./payouts.component.scss'],
  standalone: true,
  imports: [NgClass, WithdrawComponent, NgIf, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatDatepickerModule, DataTableComponent, EmptyDataComponent, AsyncPipe, TranslateModule],
})
export class PayoutsComponent implements OnInit, OnDestroy {
  filtersForm!: FormGroup;

  displayedColumns = ['address', 'amount', 'fee', 'transactionId', 'status', 'actions', 'createdAt_updatedAt'];

  initPayouts: Payouts | null = null;
  payouts: ModifierPayouts | null = null;

  initPayoutsLoaded = false;
  payoutsLoaded = false;

  isResettingForm = false;

  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  page = 0;
  count = 10;

  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private payoutsService: PayoutsService,
    private dialogService: DialogService,
    private fb: FormBuilder,
    private breakpointObserver: BreakpointObserver,
    public dialog: MatDialog,
    private notifier: NotifierService
  ) {
    this.filtersForm = this.fb.group({
      startTime: [null],
      endTime: [null],
    });
  }

  ngOnInit(): void {
    this.notifier.notifications$.pipe(takeUntil(this._unsubscribe$)).subscribe((result) => {
      if (result === 'POOL.FARM_SWITCHED' || result === 'POOL.PAYOUT_UPDATED') {
        this.notifier.clearNotifications();

        this.initPayouts = null;
        this.initPayoutsLoaded = false;
        this.resetParams();
        this.getPayouts();
      }
    });

    this.getPayouts();

    this.filtersForm.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe(() => {
      this.page = 0;

      if (!this.isResettingForm) {
        this.getPayouts();
      }
    });
  }

  getPayouts() {
    this.payoutsLoaded = false;
    this.payouts = null;
    const { startTime, endTime } = this.filtersForm.value;

    const startTimestamp = startTime ? new Date(startTime).getTime() / 1000 : undefined;
    const endTimestamp = endTime ? new Date(endTime).getTime() / 1000 : undefined;

    const params = {
      from: this.page,
      count: this.count,
      startTime: startTimestamp,
      endTime: endTimestamp,
    };

    this.payoutsService.getPayouts(params).subscribe((res) => {
      if (this.initPayoutsLoaded === false) {
        this.initPayoutsLoaded = true;
        this.payoutsService.setInitPayouts(res);
        this.initPayouts = res;
      }

      this.payoutsLoaded = true;
      this.updateData(res);
    });
  }

  pageChange(event: PageEvent): void {
    this.count = event.pageSize;
    this.page = event.pageIndex * this.count;

    this.getPayouts();
  }

  resetParams(): void {
    this.isResettingForm = true;
    this.count = 10;
    this.page = 0;
    this.filtersForm.reset();
    this.isResettingForm = false;
  }

  updateData(result: Payouts | null): void {
    this.payouts = {
      count: result?.count || 0,
      data:
        result?.data?.map((item: Payout) => ({
          ...item,
          actions: item.status === 'pending' || item.status === 'processing' ? 'cancel' : '',
          amount: `${item.amount} ${item.currency}`,
          fee: `${item.fee} ${item.currency}`,
          createdAt_updatedAt: `${Util.formatDate(new Date(item.createdAt))} 
															  ${Util.formatDate(new Date(item.updatedAt))}`,
        })) || [],
    };
  }

  cancelPayout(id: number) {
    if (!id) {
      return;
    }

    const config = {
      title: 'WM_TECH_POOL.PAYOUTS.CANCEL_POPUP.TITLE',
      app: 'pool',
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.payoutsService.cancelPayouts(id).subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
